// 情感类型
export type EmotionType = 'positive' | 'negative' | 'neutral' | 'excited' | 'sad' | 'curious' | 'angry' | 'happy';

// 情感分析结果
export interface EmotionAnalysis {
  emotion: EmotionType;
  confidence: number;
  keywords: string[];
  suggestions: string[];
}

// 情感关键词映射
const EMOTION_KEYWORDS: Record<EmotionType, string[]> = {
  positive: ['好', '棒', '喜欢', '开心', '高兴', '优秀', '厉害', '太好了', '真棒', '完美'],
  negative: ['不好', '讨厌', '不喜欢', '难过', '伤心', '糟糕', '差', '烦', '郁闷', '失望'],
  neutral: ['一般', '普通', '还行', '可以', '没什么', '无所谓', '平常', '正常'],
  excited: ['哇', '太棒了', '超赞', '激动', '兴奋', '惊喜', '不可思议', 'Amazing', 'Wow'],
  sad: ['难过', '伤心', '哭', '不开心', '失落', '沮丧', '郁闷', '痛苦', '悲伤'],
  curious: ['为什么', '怎么', '什么', '哪里', '谁', '何时', '好奇', '想知道', '了解', '学习'],
  angry: ['生气', '愤怒', '讨厌', '烦', '可恶', '讨厌死了', '气死', '火大', '不满', '抱怨'],
  happy: ['开心', '快乐', '高兴', '愉快', '喜悦', '幸福', '满足', '美好', '阳光', '笑容']
};

// 情感建议映射
const EMOTION_SUGGESTIONS: Record<EmotionType, string[]> = {
  positive: ['继续保持这种积极的态度！', '你的热情让我也很开心！', '让我们一起探索更多有趣的知识吧！'],
  negative: ['别担心，每个人都会遇到困难。', '我们可以一起想办法解决这个问题。', '告诉我更多，也许我能帮到你。'],
  neutral: ['有什么特别想了解的吗？', '我们可以聊一些更有趣的话题。', '你对什么主题感兴趣呢？'],
  excited: ['你的热情感染了我！', '让我们一起深入探索这个话题吧！', '太棒了，继续保持这种好奇心！'],
  sad: ['抱抱你，一切都会好起来的。', '愿意和我分享发生了什么吗？', '记住，我一直在你身边支持你。'],
  curious: ['这是个很棒的问题！', '我很喜欢你的好奇心！', '让我们一起来寻找答案吧！'],
  angry: ['深呼吸，冷静一下。', '我理解你的感受，让我们慢慢来。', '有什么我可以帮你的吗？'],
  happy: ['看到你开心，我也很开心！', '保持这份快乐的心情吧！', '让我们继续分享更多快乐的时刻！']
};

class EmotionService {
  
  // 分析文本情感
  public analyzeEmotion(text: string): EmotionAnalysis {
    if (!text.trim()) {
      return {
        emotion: 'neutral',
        confidence: 0.5,
        keywords: [],
        suggestions: EMOTION_SUGGESTIONS.neutral
      };
    }
    
    const lowerText = text.toLowerCase();
    let bestEmotion: EmotionType = 'neutral';
    let maxScore = 0;
    let matchedKeywords: string[] = [];
    
    // 计算每种情感的匹配分数
    for (const [emotion, keywords] of Object.entries(EMOTION_KEYWORDS)) {
      let score = 0;
      const currentMatchedKeywords: string[] = [];
      
      for (const keyword of keywords) {
        if (lowerText.includes(keyword.toLowerCase())) {
          score += 1;
          currentMatchedKeywords.push(keyword);
        }
      }
      
      if (score > maxScore) {
        maxScore = score;
        bestEmotion = emotion as EmotionType;
        matchedKeywords = currentMatchedKeywords;
      }
    }
    
    // 计算置信度
    let confidence = Math.min(maxScore / 3, 1.0); // 最多3分满分
    
    // 如果没有匹配到关键词，使用一些启发式规则
    if (maxScore === 0) {
      if (text.includes('?') || text.includes('吗') || text.includes('什么') || text.includes('为什么')) {
        bestEmotion = 'curious';
        confidence = 0.7;
      } else if (text.length > 20 && !text.includes('不') && !text.includes('没')) {
        bestEmotion = 'positive';
        confidence = 0.6;
      }
    }
    
    return {
      emotion: bestEmotion,
      confidence: confidence,
      keywords: matchedKeywords,
      suggestions: EMOTION_SUGGESTIONS[bestEmotion]
    };
  }
  
  // 根据情感调整AI回复
  public enhanceResponseWithEmotion(originalResponse: string, emotionAnalysis: EmotionAnalysis): string {
    const { emotion, confidence, suggestions } = emotionAnalysis;
    
    // 如果置信度很高，添加情感相关的建议
    if (confidence > 0.7) {
      const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)];
      return `${originalResponse}\n\n${randomSuggestion}`;
    }
    
    // 如果是中性情感，可以适当添加鼓励
    if (emotion === 'neutral' && confidence < 0.5) {
      return `${originalResponse}\n\n你觉得这个回答怎么样？有什么想继续了解的吗？`;
    }
    
    return originalResponse;
  }
  
  // 获取情感图标
  public getEmotionIcon(emotion: EmotionType): string {
    const icons: Record<EmotionType, string> = {
      positive: '😊',
      negative: '😔',
      neutral: '😐',
      excited: '🤩',
      sad: '😢',
      curious: '🤔',
      angry: '😠',
      happy: '😄'
    };
    return icons[emotion] || '🙂';
  }
}

// 创建全局情感服务实例
const emotionService = new EmotionService();

export default emotionService;